import { Link } from '@tanstack/react-router';
import {
  GRANT_SORTS,

  REGIONS,
  type GrantClosedMode,
  type GrantSort,
  type GrantSupportFilter,
} from '@zoxo/shared';
import { useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/utils';
import { GrantTotal, GrantTypeTabs, ProgramKindTabs } from './type-tabs';
import { carrySearch, CLOSED_LABEL, SORT_LABEL } from './list-state';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { GrantCard } from './grant-card';
import { useGrants, type GrantFilters } from './use-grants';
import { ListTail } from '../../components/ui/list-tail';

/**
 * 지원사업 목록.
 *
 * 필터를 콘텐츠 **위에 26개 칩으로 늘어놓지 않는다.** 참조로 삼은 기존 UI 가
 * 분야 9개 + 지역 17개를 목록보다 먼저 보여주는데, 그건 독자에게 분류 체계를
 * 먼저 배우라는 요구다. 여기서는 지역 하나만 접힌 채로 두고 정렬을 곁에 둔다 —
 * 실측상 지역이 가장 강한 필터다(지역 전용 공고: 경기 175 ↔ 세종 15).
 */


/**
 * 지원 유형.
 *
 * **「금액 확인된 것만」을 대신한다.** 그 필터는 「금액이 있나 없나」만 물어서
 * 융자 1건과 프로그램 셋을 가진 공고를, 보조금 하나뿐인 공고와 구별하지 못했다.
 * 게다가 실측 절반 이상인 프로그램형 지원은 **금액이 없는 게 정상**이라, 그
 * 필터를 켜면 실제 지원이 있는 공고가 대량으로 사라졌다.
 *
 * 「원문 전체」도 뺐다. 그건 운영용 컨트롤이었고, 독자에게 「글자 없음」·「못
 * 읽음」은 우리 배치의 사정이지 공고의 성질이 아니다. 그 확인은 콘솔에서 한다.
 */
/**
 * 마감 공고 취급.
 *
 * **「마감만」이 있는 이유:** 지원사업은 대부분 매년 반복되므로 작년 공고가
 * 올해 신청자에게 가장 값진 자료다. 마감을 「숨길 것」으로만 다루면 그 자료로
 * 가는 길이 없다 — `news.$id.tsx` 가 마감 공고에도 댓글을 열어 두는 것과
 * 같은 판단이다.
 */

interface Props {
  filters: GrantFilters;
  /** 마감 계산 기준 — loader 가 정한 시각. 카드마다 now 가 달라지면 안 되고, 서버와 브라우저도 같아야 한다. */
  now: Date;
  onChange: (next: GrantFilters) => void;
  /** 유형 전용 화면(`/loans` 등). 유형 드롭다운을 감춘다 — 주소가 이미 정했다. */
  lockSupport?: boolean;
}

export function GrantList({ filters, now, onChange, lockSupport = false }: Props) {
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGrants(filters);
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
      },
      { rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const grants = data?.pages.flatMap((p) => p.data) ?? [];
  const sort = filters.sort ?? 'latest';

  /*
    검색어는 **입력 중에는 URL 을 건드리지 않는다.** 글자마다 필터를 바꾸면
    타이핑 한 번에 요청이 여섯 번 나가고 목록이 깜빡인다. Enter 나 포커스를
    벗어날 때 확정한다. URL 의 q 가 바뀌면(뒤로가기 등) 입력값도 따라간다.
  */
  const [term, setTerm] = useState(filters.q ?? '');
  useEffect(() => setTerm(filters.q ?? ''), [filters.q]);
  const commitTerm = () => {
    const next = term.trim() || undefined;
    if (next !== filters.q) onChange({ ...filters, q: next });
  };

  return (
    <section>
      {/*
        **머리와 함께 붙어 있다.** 머리(로고 48 + 숨 8 + 탭 33 = 89px) 바로 아래에 sticky 로
        앉아, 목록을 아무리 내려도 검색·지역·마감·정렬이 손닿는 곳에 있다. 페이지
        컨테이너가 overflow-hidden 이면 sticky 가 죽는다 — 목록 라우트는 allowOverflow.
      */}
      <div className="sticky top-12 z-[400] -mx-4 -mt-4 flex flex-wrap items-center gap-2 border-b border-gray-100 bg-surface px-4 pb-2 pt-4 sm:top-[89px]">
        {/* 기둥 알약 — 전체 | 자금·바우처 | 융자·보증 | 시설·입주·공간 | 프로그램. */}
        <GrantTypeTabs
          active={filters.support ?? null}
          search={carrySearch(filters)}
          link={({ href, search, className, children }) => (
            <Link to={href} search={search as never} className={className}>{children}</Link>
          )}
        />
        {/* 프로그램 탭 안의 하위 종류 알약 (`?kind=`). */}
        {filters.support === 'program' ? (
          <ProgramKindTabs
            active={filters.kind ?? null}
            search={carrySearch(filters)}
            link={({ href, search, className, children }) => (
              <Link to={href} search={search as never} className={className}>{children}</Link>
            )}
          />
        ) : null}
        {/*
          **네이티브 `<select>` 를 버렸다.** 브라우저 기본 셀렉트는 OS 마다
          다르게 그려져 나머지 UI 와 따로 논다 — 높이도 글꼴도 화살표도 CSS 로
          맞출 수 없다. shadcn/radix Select 는 트리거가 그냥 버튼이라 우리
          디자인이 그대로 먹고, 키보드·ARIA 는 radix 가 준다.

          **값 없음을 `''` 로 두지 않는다.** radix 는 빈 문자열을 「선택 안 됨」
          으로 다뤄 placeholder 로 되돌린다. 「전체 지역」은 엄연한 선택지라
          `all` 이라는 값을 주고, 바깥으로 나갈 때 `undefined` 로 바꾼다.
        */}
        <Input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onBlur={commitTerm}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitTerm();
            }
          }}
          placeholder="제목·기관 검색"
          aria-label="검색"
          className="h-9 w-[180px] border-gray-200 bg-white"
        />

        <Select
          value={filters.region ?? 'all'}
          onValueChange={(v) => onChange({ ...filters, region: v === 'all' ? undefined : v })}
        >
          <SelectTrigger aria-label="지역" className="w-[124px]">
            {/* 글자를 직접 준다 — Radix 는 선택값 글자를 브라우저에서만 채워 서버 HTML 의 막대가 빈다. */}
            <SelectValue>{filters.region ?? '전체 지역'}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>지역</SelectLabel>
              <SelectItem value="all">전체 지역</SelectItem>
              {REGIONS.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {/*
          **유형 전용 화면에서는 이 드롭다운을 감춘다** (`/loans` 등).

          주소가 유형을 정하는데 화면에서 또 고르게 두면 둘이 어긋난다 —
          주소는 융자인데 보조금이 보이는 상태가 만들어진다. 유형을 바꾸는
          것은 다른 주소로 가는 일이지 필터를 만지는 일이 아니다.
        */}
        {/* 유형 선택 상자는 없다 — 머리의 보조금·바우처·융자·보증·프로그램 알약이 그 일을 한다. */}
        <Select
          value={filters.closed ?? 'exclude'}
          onValueChange={(v) =>
            onChange({ ...filters, closed: v === 'exclude' ? undefined : (v as GrantClosedMode) })
          }
        >
          <SelectTrigger aria-label="마감 여부" className="w-[116px]">
            <SelectValue>{CLOSED_LABEL[filters.closed ?? 'exclude']}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>마감</SelectLabel>
              {(Object.keys(CLOSED_LABEL) as GrantClosedMode[]).map((k) => (
                <SelectItem key={k} value={k}>{CLOSED_LABEL[k]}</SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {/* 정렬 — 지역·마감과 같은 shadcn Select. */}
        <Select value={sort} onValueChange={(v) => onChange({ ...filters, sort: v as GrantSort })}>
          <SelectTrigger aria-label="정렬" className="w-[124px] sm:ml-auto">
            <SelectValue>{SORT_LABEL[sort]}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>정렬</SelectLabel>
              {GRANT_SORTS.map((s) => (
                <SelectItem key={s} value={s}>{SORT_LABEL[s]}</SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {/* 전체 건수 — 정렬 오른쪽. */}
        <GrantTotal total={data?.pages[0]?.meta.total} />
      </div>

      {isError ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-600">공고를 불러오지 못했습니다.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-3 h-11 rounded-lg border border-gray-200 px-4 text-sm text-gray-800 hover:bg-gray-50"
          >
            다시 시도
          </button>
        </div>
      ) : isLoading ? (
        <ul className="divide-y divide-gray-100" aria-label="불러오는 중">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i} className="flex gap-6 py-5">
              <div className="h-6 w-28 shrink-0 animate-pulse rounded bg-gray-100" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100" />
                <div className="h-3 w-1/3 animate-pulse rounded bg-gray-100" />
              </div>
            </li>
          ))}
        </ul>
      ) : grants.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-600">조건에 맞는 공고가 없습니다.</p>
          <button
            type="button"
            onClick={() => onChange({ sort })}
            className="mt-3 h-11 rounded-lg border border-gray-200 px-4 text-sm text-gray-800 hover:bg-gray-50"
          >
            필터 지우기
          </button>
        </div>
      ) : (
        <>
          <div>
            {grants.map((g) => (
              <GrantCard key={g.id} grant={g} now={now} support={filters.support} />
            ))}
          </div>
          <div ref={sentinel} aria-hidden className="h-px" />
          <ListTail fetching={isFetchingNextPage} done={!hasNextPage && grants.length > 0} />
        </>
      )}
    </section>
  );
}
